import styled from "@emotion/styled";
import { VisibilityOutlined } from "@mui/icons-material";
import {
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  IconEdit,
  IconPlus,
  IconTrash,
  IconDownload,
} from "@tabler/icons-react";
import SendIcon from "@mui/icons-material/Send";
import ResendMailConfirmation from "../../components/ResendMailConfirmation";
import moment from "moment";
import { useModal } from "mui-modal-provider";
import { useCallback, useMemo, useState } from "react";
import useAuth from "../../../hooks/useAuth";
import useLetters from "../../../hooks/useLetters";
import useUser from "../../../hooks/useUser";
import SubCard from "../../components/cards/SubCard";
import NoResultFound from "../../components/NoResultFound";
import { useSnackbarHelper } from "../../components/Snackbar";
import DeleteDocumentModal from "./DeleteDocumentModal";
import DeleteLetterModal from "./DeleteLetterModal";
import DigitalDocumentModal from "./DigitalDocumentModal";
import PreviewPdfModal from "./PreviewPdfModal";
import SelectLetterModal from "./SelectLetterModal";
import ViewDigitalDocument from "./ViewDigitalDocument";
import { LETTERS } from "../../../constants/MenuSelection";
import Pagination from "../../components/Pagination";
import useResendMail from "../../../hooks/useResendMail";
import SyncIcon from "@mui/icons-material/Sync";
import useCases from "../../../hooks/useCases";
import { getS3Url } from "../../../utils/s3";
import DocExplorerDocuments from "../docExplorer/Documents";

function Documents({ caseData, isCountyInmate }: any) {
  const showSnackbar = useSnackbarHelper();
  const { refetchCase } = useCases(caseData?._id);

  const options = [
    { title: "All Document", value: "allDocuments" },
    { title: "Chat Document", value: "chatDocuments" },
    { title: "Rep Document", value: "repAgreement" },
    { title: "DPS Report", value: "report" },
  ];

  const { userId } = useAuth();
  const { isStaff } = useUser(userId);
  const { showModal } = useModal();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [resendMailId, setResendMailId] = useState(null);
  const [selectedValue, setSelectedValue] = useState(options[0]?.value); // Set initial value to the first option
  const [rotated, setRotated] = useState(false);
  const [letterLoading, setLetterLoading] = useState<any>(null);
  const [documentLoading, setDocumentLoading] = useState<any>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState();

  const { resendLetterMail, isLoading } = useResendMail();

  const CustomTableRow = styled(TableRow)(({ theme }) => ({
    height: "50px",
  }));

  const CustomTableCell = styled(TableCell)(({ theme }) => ({
    paddingTop: 0,
    paddingBottom: 0,
    width: 130,
    overflow: "hidden",
    textOverflow: "ellipsis",
  }));

  const { getLetterData, isLetterLoading } = useLetters({
    caseId: caseData?._id,
  });

  const handleShowLetterPreview = useCallback(
    (Buffer: any) => {
      const modal: any = showModal(PreviewPdfModal, {
        onClose: () => modal.hide(),
        buffer: Buffer,
      });
    },
    [showModal],
  );

  const handleViewLetter = async (template: any) => {
    try {
      const response = await getLetterData({
        template: template,
        sendMail: false,
      });
      if (response) {
        setLetterLoading(null);
      }
      handleShowLetterPreview(response?.data);
    } catch (error: any) {
      const message = error?.response?.data?.message || "An error occurred";

      showSnackbar(message, "error");
    }
  };

  const handleAdd = useCallback(() => {
    const modal: any = showModal(SelectLetterModal, {
      onClose: () => {
        modal.hide();
      },
      caseId: caseData?._id,
      caseData: caseData,
      handleShowLetterPreview,
    });
  }, [showModal, handleShowLetterPreview, caseData]);

  const handleAddDigitalDocument = useCallback(() => {
    const modal: any = showModal(DigitalDocumentModal, {
      onClose: () => modal.hide(),
      caseId: caseData?._id,
      uniqueCaseId: caseData?.caseId,
      inmateName: caseData?.contactId?.inmate?.name
        ? caseData?.contactId?.inmate?.name
        : "County_Inmate",
    });
  }, [showModal, caseData]);

  const handleEdit = useCallback(
    (data: any) => {
      const modal: any = showModal(DigitalDocumentModal, {
        onClose: () => modal.hide(),
        caseId: caseData?._id,
        uniqueCaseId: caseData?.caseId,
        inmateName: caseData?.contactId?.inmate?.name
          ? caseData?.contactId?.inmate?.name
          : "County_Inmate",
        data: data,
      });
    },
    [showModal, caseData],
  );

  // const handleViewDocument = useCallback(
  //   (data: any) => {
  //     const modal: any = showModal(ViewDigitalDocument, {
  //       onClose: () => modal.hide(),
  //       documentUrl: data?.document[data?.document.length - 1]?.url,
  //       mimeType: data?.type,
  //     });
  //   },
  //   [showModal]
  // );
  const handleViewDocument = useCallback(
    (data: any) => {
      setDocumentLoading(data?._id); // Set loading state
      const modal: any = showModal(ViewDigitalDocument, {
        onClose: () => {
          modal.hide();
        },
        fileUrl: getS3Url(data?.document[data?.document?.length - 1]?.url),
        mimetype: data?.type,
        title: data?.title,
        closeLoading: setDocumentLoading,
      });
    },
    [showModal],
  );

  const handleDeleteLetter = useCallback(
    (letterId: any) => {
      const modal: any = showModal(DeleteLetterModal, {
        onClose: () => modal.hide(),
        caseId: caseData?._id,
        letterId: letterId,
      });
    },
    [showModal, caseData],
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedValue(event.target.value);
  };

  const selectedOption = options?.find(
    (option) => option.value === selectedValue,
  );

  const handleDeleteDocument = useCallback(
    (documentId: any) => {
      const modal: any = showModal(DeleteDocumentModal, {
        onClose: () => modal.hide(),
        caseId: caseData?._id,
        documentId: documentId,
      });
    },
    [showModal, caseData],
  );
  //---------------------------------------------------------------
  const caseLetters = caseData?.letters?.filter(
    (letter: any) => letter?.isDeleted !== true,
  );
  const caseDocuments = caseData?.documents?.filter(
    (document: any) =>
      document?.isDeleted !== true && !document.isMessageAttachment,
  );

  const handleDownload = async (fileUrl: string, fileName: string) => {
    try {
      setIsDownloading(true);
      const response = await fetch(fileUrl, { method: "GET" });
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", fileName); // Force download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Revoke object URL after download
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
      setIsDownloading(false);
    } catch (error) {
      console.error("Download error:", error);
      setIsDownloading(false);
    }
  };
  // Sliced rows based on pagination
  const paginatedRows = caseDocuments.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  function shortenFileName(fileName: string, maxLength: number = 20): string {
    const extensionIndex = fileName.lastIndexOf(".");

    // Handle files without extensions
    if (extensionIndex === -1 || fileName.length <= maxLength) {
      return fileName; // Return original if it's shorter than maxLength
    }

    const name = fileName.substring(0, extensionIndex); // File name without extension
    const extension = fileName.substring(extensionIndex); // File extension

    if (fileName.length <= maxLength) {
      return fileName;
    }

    // Start part (first N characters) and end part (last N characters of name)
    const startPart = name.substring(0, 15);
    const endPart = name.substring(name.length - 5);

    return `${startPart}..${endPart}${extension}`;
  }

  const handleResendMail = useCallback(
    (data: any) => {
      data.caseId = caseData?._id;
      const modal: any = showModal(ResendMailConfirmation, {
        onClose: () => modal.hide(),
        onSubmit: async () => {
          try {
            const result = await resendLetterMail(data);
            const message = result?.message || "Success";
            showSnackbar(message, "success");
          } catch (error: any) {
            const message =
              error?.response?.result?.message || "An error occurred";
            showSnackbar(message, "error");
          }
        },
        title: "Resend Mail",
        content: "Do you really want to resend this email?",
      });
    },
    [showModal, resendLetterMail],
  );

  // const handleResendMail = async (data: any) => {
  //   try {
  //     setResendMailId(data?._id);
  //     data.caseId = caseData?._id;
  //     const result = await resendLetterMail(data);
  //     const message = result?.message || "Success";
  //     showSnackbar(message, "success");
  //     if (result) {
  //       setResendMailId(null);
  //     }
  //   } catch (error: any) {
  //     const message = error?.response?.result?.message || "An error occurred";
  //     setResendMailId(null);
  //     showSnackbar(message, "error");
  //   }
  // };
  const handleRefetch = () => {
    setRotated((prev) => !prev);
    refetchCase();
  };

  return (
    <Grid container spacing={2}>
      {!isCountyInmate && (
        <Grid item xs={12}>
          <SubCard
            title="Forms"
            secondary={
              <Tooltip title="Add letter">
                {/* {isStaff ? (
                <Typography></Typography>
              ) : ( */}
                <Button
                  sx={{
                    color: "green",
                    cursor: "pointer",
                  }}
                  onClick={handleAdd}
                >
                  <IconPlus />
                </Button>
                {/* )} */}
              </Tooltip>
            }
          >
            {!!caseLetters?.length ? (
              <Stack sx={{ overflow: "auto" }}>
                <Table>
                  {/* Wrap header in <TableHead> */}
                  <TableHead>
                    <CustomTableRow
                      sx={{ fontSize: { sm: "14px", md: "15px" } }}
                    >
                      <CustomTableCell sx={{ minWidth: 50 }}>
                        <Typography variant="h5">Mode</Typography>
                      </CustomTableCell>
                      <CustomTableCell sx={{ minWidth: 50 }}>
                        <Typography variant="h5">Type</Typography>
                      </CustomTableCell>
                      <CustomTableCell sx={{ minWidth: 200 }}>
                        <Typography variant="h5">Letter</Typography>
                      </CustomTableCell>
                      <CustomTableCell sx={{ minWidth: 130 }}>
                        <Typography variant="h5">Date</Typography>
                      </CustomTableCell>
                      {/* <CustomTableCell>
                      <Typography variant="h5">Docusign Information</Typography>
                    </CustomTableCell> */}
                      <CustomTableCell sx={{ textAlign: "center" }}>
                        <Typography variant="h5" sx={{ minWidth: 200 }}>
                          Action
                        </Typography>
                      </CustomTableCell>
                    </CustomTableRow>
                  </TableHead>

                  {/* Wrap table body in <TableBody> */}
                  <TableBody>
                    {caseData?.letters?.map(
                      (row: any, index: any) =>
                        !row?.isDeleted && (
                          <CustomTableRow
                            key={index}
                            sx={{ fontSize: { sm: "14px", md: "15px" } }}
                          >
                            <CustomTableCell
                              sx={{ textTransform: "capitalize" }}
                            >
                              {row?.mode ? `${row?.mode}` : `-`}
                            </CustomTableCell>
                            <CustomTableCell>{row?.type}</CustomTableCell>
                            <CustomTableCell>
                              {LETTERS?.find(
                                (letter: any) => letter?.value === row?.title,
                              )?.label || "-"}
                            </CustomTableCell>
                            <CustomTableCell>
                              {moment(row?.date).format("MM/DD/YYYY, h:mm A")}
                            </CustomTableCell>
                            {/* <CustomTableCell>
                            {row?.docuSignInfo ? `${row?.docuSignInfo}` : "-"}
                          </CustomTableCell> */}
                            <CustomTableCell sx={{ textAlign: "center" }}>
                              {letterLoading === row?._id ? (
                                <IconButton
                                  sx={{ cursor: "default" }}
                                  disableRipple
                                >
                                  <CircularProgress size="25px" />
                                </IconButton>
                              ) : (
                                <Tooltip title={"View Letter"}>
                                  <span>
                                    <IconButton
                                      onClick={() => {
                                        handleViewLetter(row?.title);
                                        setLetterLoading(row?._id);
                                      }}
                                      disabled={letterLoading === row?._id}
                                    >
                                      <VisibilityOutlined />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}

                              {/* {!isStaff && ( */}
                              <Tooltip title={"Delete Letter"}>
                                <IconButton
                                  onClick={() => handleDeleteLetter(row?._id)}
                                >
                                  <IconTrash style={{ color: "#D83630" }} />
                                </IconButton>
                              </Tooltip>
                              {isLoading && row?._id === resendMailId ? (
                                <IconButton
                                  sx={{ cursor: "default" }}
                                  disableRipple
                                >
                                  <CircularProgress size="25px" />
                                </IconButton>
                              ) : (
                                <Tooltip title="Resend letter Email">
                                  {/* <span> */}
                                  <IconButton
                                    onClick={() => {
                                      handleResendMail(row);
                                    }}
                                  >
                                    <SendIcon style={{ color: "#1976d2" }} />
                                  </IconButton>
                                  {/* </span> */}
                                </Tooltip>
                              )}
                              {/* )} */}
                            </CustomTableCell>
                          </CustomTableRow>
                        ),
                    )}
                  </TableBody>
                </Table>
              </Stack>
            ) : (
              <NoResultFound>No Form Found</NoResultFound>
            )}
          </SubCard>
        </Grid>
      )}
      <Grid item xs={12}>
        <FormControl>
          <RadioGroup row value={selectedValue} onChange={handleChange}>
            {options?.map((option: any, index: any) => (
              <FormControlLabel
                key={index}
                value={option?.value}
                control={<Radio color="error" />}
                label={option?.title}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </Grid>
      {selectedValue !== "allDocuments" ? (
        <DocExplorerDocuments
          selectedCase={caseData}
          selectedOption={selectedOption}
          fromCase
        />
      ) : (
        <Grid item xs={12}>
          <SubCard
            title="Documents"
            secondary={
              <>
                <Tooltip title={"Refetch Documents"}>
                  <IconButton
                    disableRipple
                    onClick={() => handleRefetch()}
                    id="Refetch-document"
                  >
                    <SyncIcon
                      style={{
                        transform: rotated ? "rotate(360deg)" : "rotate(0deg)",
                        transition: "transform 1s ease",
                      }}
                    />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Add documents">
                  <Button
                    sx={{
                      color: "green",
                      cursor: "pointer",
                    }}
                    onClick={handleAddDigitalDocument}
                  >
                    <IconPlus />
                  </Button>
                </Tooltip>
              </>
            }
          >
            {!!paginatedRows?.length ? (
              <Stack sx={{ overflow: "auto" }}>
                <Table sx={{ tableLayout: "fixed" }}>
                  <TableHead>
                    <CustomTableRow
                      sx={{ fontSize: { sm: "10px", md: "15px" } }}
                    >
                      <CustomTableCell>
                        <Typography variant="h5">Created At</Typography>
                      </CustomTableCell>
                      <CustomTableCell>
                        <Typography variant="h5">Updated At</Typography>
                      </CustomTableCell>
                      <CustomTableCell>
                        <Typography variant="h5">Title</Typography>
                      </CustomTableCell>
                      <CustomTableCell>
                        <Typography variant="h5">Uploaded By</Typography>
                      </CustomTableCell>
                      <CustomTableCell>
                        <Typography variant="h5">Action</Typography>
                      </CustomTableCell>
                    </CustomTableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedRows?.map(
                      (row: any, index: any) =>
                        !row?.isDeleted && (
                          <CustomTableRow
                            key={index}
                            sx={{ fontSize: { sm: "10px", md: "15px" } }}
                          >
                            <CustomTableCell>
                              {moment(row?.createdAt).format("MM/DD/YYYY")}
                            </CustomTableCell>
                            <CustomTableCell>
                              {moment(row?.updatedAt).format("MM/DD/YYYY")}
                            </CustomTableCell>
                            <CustomTableCell>
                              {shortenFileName(row?.title)}
                            </CustomTableCell>
                            <CustomTableCell>
                              {row?.uploadedBy?.userName}
                            </CustomTableCell>
                            <CustomTableCell>
                              {!row?.isMessageAttachment ? (
                                // <IconButton onClick={() => handleEdit(row)}>
                                //   <Tooltip title={"Edit Document"}>
                                //     <IconEdit color="grey" cursor={"pointer"} />
                                //   </Tooltip>
                                // </IconButton>
                                <></>
                              ) : isDownloading && selectedDoc === index ? (
                                <CircularProgress
                                  size={24}
                                  sx={{ mt: 1, mr: 2 }}
                                />
                              ) : (
                                <IconButton
                                  onClick={() => {
                                    setSelectedDoc(index);
                                    handleDownload(
                                      getS3Url(row?.document?.[0]?.url),
                                      row?.document?.[0]?.name,
                                    );
                                  }}
                                >
                                  <Tooltip title={"Download Document"}>
                                    <IconDownload
                                      color="grey"
                                      cursor={"pointer"}
                                    />
                                  </Tooltip>
                                </IconButton>
                              )}
                              <Tooltip title={"View Document"}>
                                <IconButton
                                  onClick={() => handleViewDocument(row)}
                                >
                                  {documentLoading === row?._id ? (
                                    <CircularProgress size={"25px"} />
                                  ) : (
                                    <VisibilityOutlined />
                                  )}
                                </IconButton>
                              </Tooltip>
                              {!row?.isMessageAttachment && (
                                <Tooltip title={"Delete Document"}>
                                  <IconButton
                                    onClick={() =>
                                      handleDeleteDocument(row?._id)
                                    }
                                  >
                                    <IconTrash style={{ color: "#D83630" }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </CustomTableCell>
                          </CustomTableRow>
                        ),
                    )}
                  </TableBody>
                </Table>
                <Pagination
                  component="div"
                  count={caseDocuments?.length || 0}
                  page={page}
                  setPage={setPage}
                  rowsPerPage={rowsPerPage}
                  setRowsPerPage={setRowsPerPage}
                  rowsPerPageOptions={[5, 10, 20]}
                />
              </Stack>
            ) : (
              <NoResultFound>No Document Found</NoResultFound>
            )}
          </SubCard>
        </Grid>
      )}
    </Grid>
  );
}

export default Documents;
